<div class="duplicate-push-flow">

  <!-- ── Stepper (non-numbered, mirrors push-to-group) ────────────── -->
  <div class="header">
    <app-stepper
      *ngIf="!isOnTerminalStep"
      [steps]="steps"
      [selectedStep]="selectedStep"
      (selectedStepChange)="selectedStep = $event"
    ></app-stepper>
  </div>

  <div class="body">

    <!-- ── Step: Select Dates + Times ─────────────────────────────── -->
    <ng-container *ngIf="currentStep === 'select-dates'">
      <div class="section-title">
        <span class="ksubtitle-medium">Pick the dates you want to duplicate this course onto</span>
        <span class="klabel-medium subtitle">
          You can pick any date within the current school year — past or future.
        </span>
      </div>

      <div class="date-step-grid">
        <div class="calendar-pane">
          <app-multi-date-calendar
            [selectedDates]="selectedDates"
            [minDate]="schoolYearStartDate"
            (datesChange)="onDatesChanged($event)"
          ></app-multi-date-calendar>
        </div>

        <div class="summary-pane">
          <div class="summary-header">
            <span class="ksubtitle-medium">Selected dates ({{ selectedDates.length }})</span>
          </div>

          <div class="summary-list" *ngIf="selectedDates.length > 0; else emptyDates">
            <div
              class="summary-row"
              *ngFor="let dateIso of sortedSelectedDates"
            >
              <div class="row-info">
                <mat-icon
                  fontSet="material-icons-round"
                  class="row-icon"
                >event</mat-icon>
                <div class="date-text">
                  <span class="kbody-medium">{{ dateIso | date: 'EEE, MMM d, y' }}</span>
                  <span
                    class="klabel-medium session-count"
                    *ngIf="sessionCountForDate(dateIso) > 1"
                  >{{ sessionCountForDate(dateIso) }} sessions</span>
                </div>
              </div>
              <button
                type="button"
                class="remove-btn"
                [attr.aria-label]="'Remove ' + dateIso"
                (click)="removeDate(dateIso)"
              >
                <mat-icon fontSet="material-icons-round">close</mat-icon>
              </button>
            </div>
          </div>

          <ng-template #emptyDates>
            <div class="empty-state klabel-medium">
              No dates selected yet. Click any date within the current school year to begin.
            </div>
          </ng-template>
        </div>
      </div>

      <!-- Course Time table -->
      <div class="course-time-section" *ngIf="sortedDateEntries.length > 0">
        <div class="course-time-header">
          <mat-icon fontSet="material-icons-round" class="time-section-icon">schedule</mat-icon>
          <span class="ksubtitle-medium">Course time <span class="required-mark">*</span></span>
          <span
            class="klabel-medium validation-badge warning"
            *ngIf="hasIncompleteDateTimes"
          >
            <mat-icon fontSet="material-icons-round" class="badge-icon">warning</mat-icon>
            Complete all start and end times to continue
          </span>
        </div>

        <div class="course-time-table">
          <div class="time-table-head">
            <span class="col-date klabel-medium">Date</span>
            <span class="col-time klabel-medium">Start time <span class="required-mark">*</span></span>
            <span class="col-time klabel-medium">End time <span class="required-mark">*</span></span>
            <span class="col-remove"></span>
          </div>

          <ng-container *ngFor="let entry of sortedDateEntries; let i = index">
            <div
              class="time-table-row"
              [class.incomplete]="!isDateEntryTimeComplete(entry)"
            >
              <div class="col-date">
                <mat-icon fontSet="material-icons-round" class="date-icon">event</mat-icon>
                <span class="kbody-medium">{{ entry.date | date: 'EEE, MMM d, y' }}</span>
              </div>

              <div class="col-time">
                <input
                  type="time"
                  class="kinput"
                  [ngModel]="entry.startTime"
                  [ngModelOptions]="{ standalone: true }"
                  (ngModelChange)="entry.startTime = $event || null"
                  aria-label="Start time"
                  [class.invalid]="!entry.startTime"
                />
              </div>

              <div class="col-time">
                <input
                  type="time"
                  class="kinput"
                  [ngModel]="entry.endTime"
                  [ngModelOptions]="{ standalone: true }"
                  (ngModelChange)="entry.endTime = $event || null"
                  aria-label="End time"
                  [class.invalid]="!entry.endTime"
                />
              </div>

              <div class="col-remove">
                <button
                  type="button"
                  class="remove-entry-btn"
                  [attr.aria-label]="'Remove session on ' + entry.date"
                  (click)="removeDateEntry(entry)"
                >
                  <mat-icon fontSet="material-icons-round">close</mat-icon>
                </button>
              </div>
            </div>

            <div
              class="add-session-row"
              *ngIf="isLastEntryForDate(i, sortedDateEntries)"
            >
              <button
                type="button"
                class="add-session-btn klabel-medium"
                (click)="addSessionForDate(entry.date)"
              >
                <mat-icon fontSet="material-icons-round">add</mat-icon>
                Add another session on {{ entry.date | date: 'MMM d' }}
              </button>
            </div>
          </ng-container>
        </div>
      </div>

      <!-- Additional options -->
      <div class="additional-options-section">
        <div class="additional-options-header">
          <mat-icon fontSet="material-icons-round" class="options-icon">tune</mat-icon>
          <span class="ksubtitle-medium">
            Additional options for <strong>selected courses only</strong>
          </span>
        </div>

        <div class="push-option-cards">
          <label class="push-option-card" [class.selected]="pushOption === 'copy-existing'">
            <input
              type="radio"
              name="pushOption"
              value="copy-existing"
              [(ngModel)]="pushOption"
              [ngModelOptions]="{ standalone: true }"
              (ngModelChange)="updateWizard()"
            />
            <div class="push-option-card-content">
              <mat-icon fontSet="material-icons-round" class="option-icon">people</mat-icon>
              <div class="option-text">
                <span class="kbody-medium option-title">Copy Existing Registrations</span>
                <span class="klabel-medium option-desc">Carry over current registrations to the duplicated courses</span>
              </div>
              <mat-icon
                fontSet="material-icons-round"
                class="option-check"
                [style.visibility]="pushOption === 'copy-existing' ? 'visible' : 'hidden'"
              >check_circle</mat-icon>
            </div>
          </label>

          <label class="push-option-card" [class.selected]="pushOption === 'select-new'">
            <input
              type="radio"
              name="pushOption"
              value="select-new"
              [(ngModel)]="pushOption"
              [ngModelOptions]="{ standalone: true }"
              (ngModelChange)="updateWizard()"
            />
            <div class="push-option-card-content">
              <mat-icon fontSet="material-icons-round" class="option-icon">send</mat-icon>
              <div class="option-text">
                <span class="kbody-medium option-title">Select New Push Information</span>
                <span class="klabel-medium option-desc">Choose a push type and target groups for these duplicates</span>
              </div>
              <mat-icon
                fontSet="material-icons-round"
                class="option-check"
                [style.visibility]="pushOption === 'select-new' ? 'visible' : 'hidden'"
              >check_circle</mat-icon>
            </div>
          </label>
        </div>
      </div>
    </ng-container>

    <!-- ── Step: Select Push Type ───────────────────────────── -->
    <ng-container *ngIf="currentStep === 'select-push-params'">
      <span class="klabel-huge">Select Push Type</span>
      <div class="radio-row" *ngFor="let group of pushGroups">
        <input
          type="radio"
          name="pushGroup"
          [id]="group.kalpaIdentifier"
          [value]="group.kalpaIdentifier"
          [(ngModel)]="selectedPushGroup"
          [ngModelOptions]="{ standalone: true }"
          (change)="onPushGroupSelected()"
        >
        <label [for]="group.kalpaIdentifier" class="kbody-large">{{ getPushGroupDisplayLabel(group) }}</label>
      </div>
    </ng-container>

    <!-- ── Step: Select Requirement ────────────────────────────────── -->
    <ng-container *ngIf="currentStep === 'select-requirement'">
      <div class="req-screen">
        <span class="ktitle-tiny">Select Requirement</span>
        <p class="kbody-medium req-body">
          The requirement you select here determines where this course will be categorized in each user's PD plan.
        </p>
        <p class="kbody-medium req-body">
          If a user does not have the chosen requirement in their PD plan, the course will be placed in their 'Portfolio/Unassigned' bucket automatically.
        </p>
        <div class="req-field">
          <span class="klabel-medium req-label">Select Requirement</span>
          <app-select
            [options]="formattedCourseRequirements"
            value="{{ this?.coursePushPackage?.requirement?.id }}"
            [placeholder]="'Select a requirement'"
            (selectedValue)="updateCoursePushPackageParameter($event, 'requirement')"
            class="requirement-selecter"
          ></app-select>
        </div>
      </div>
    </ng-container>

    <!-- ── Step: Select Buildings ──────────────────────────────────── -->
    <ng-container *ngIf="currentStep === 'select-buildings-new'">
      <span class="klabel-huge">Select building(s)</span>
      <div class="detail">
        <span class="klabel-medium label">Select buildings this course can be applied to*</span>
        <div class="input-select-container">
          <input
            #buildingsinput
            (change)="updateCoursePushPackageParameter($event, 'buildings')"
            class="kinput kbody-medium"
            type="text"
            placeholder="Type or select a building name"
            list="buildingtargets-dup"
          >
          <datalist id="buildingtargets-dup">
            <option class="kbody-medium" *ngFor="let building of filteredBuildings">{{ building.buildingName }}</option>
          </datalist>
        </div>
        <span class="klabel-medium label" *ngIf="coursePushPackage?.buildings?.length > 0">Selections</span>
        <div class="chips">
          <app-chip
            class="outlined"
            (closeChip)="removeBuilding(building)"
            [closable]="true"
            *ngFor="let building of coursePushPackage.buildings"
            [text]="building.buildingName"
          ></app-chip>
        </div>
      </div>
    </ng-container>

    <!-- ── Step: Select Positions ──────────────────────────────────── -->
    <ng-container *ngIf="currentStep === 'select-positions'">
      <span class="klabel-huge">Select position(s)</span>
      <div class="detail">
        <span class="klabel-medium label">Select positions this course can be applied to*</span>
        <div class="input-select-container">
          <input
            #positionsinput
            (change)="updateCoursePushPackageParameter($event, 'positions')"
            class="kinput kbody-medium"
            type="text"
            placeholder="Type or select a position name"
            list="positiontargets-dup"
          >
          <datalist id="positiontargets-dup">
            <option class="kbody-medium" *ngFor="let position of sortedEntityPositions">{{ position.positionTitle }}</option>
          </datalist>
        </div>
        <span class="klabel-medium label" *ngIf="coursePushPackage?.positions?.length > 0">Selections</span>
        <div class="chips">
          <app-chip
            class="outlined"
            (closeChip)="removePosition(position)"
            [closable]="true"
            *ngFor="let position of coursePushPackage.positions"
            [text]="position.positionTitle"
          ></app-chip>
        </div>
      </div>
    </ng-container>

    <!-- ── Step: Select Users (quick people push parity) ───────────── -->
    <ng-container *ngIf="currentStep === 'select-users'">
      <span class="klabel-huge">Select user(s)</span>
      <div class="detail">
        <span class="klabel-medium label">Select user(s) this course should be pushed to*</span>
        <div class="input-select-container">
          <input
            #userinput
            (change)="updateCoursePushPackageParameter($event, 'users')"
            class="kinput kbody-medium"
            type="text"
            placeholder="Type or select a user name"
            list="usertargets-dup"
          >
          <datalist id="usertargets-dup">
            <option class="kbody-medium" *ngFor="let user of sortedUsers">{{ user.fullName }}</option>
          </datalist>
        </div>
        <span class="klabel-medium label" *ngIf="coursePushPackage?.users?.length > 0">Selections</span>
        <div class="chips">
          <app-chip
            class="outlined"
            (closeChip)="removeUser(user)"
            [closable]="true"
            *ngFor="let user of coursePushPackage.users"
            [text]="user.fullName"
          ></app-chip>
        </div>
        <span class="klabel-medium label">Additional notes for email (optional)</span>
        <textarea
          [(ngModel)]="coursePushPackage.messageContent"
          [ngModelOptions]="{ standalone: true }"
          rows="4"
          class="kinput msg-input kbody-medium"
          placeholder="Optional message body when recipients are notified..."
        ></textarea>
      </div>
    </ng-container>

    <!-- ── Step: Send Message ──────────────────────────────────────── -->
    <ng-container *ngIf="currentStep === 'send-message'">
      <div class="msg-screen">
        <span class="ktitle-tiny">Send Message (optional)</span>
        <p class="kbody-medium msg-description">
          Automatically send an email to all users who receive this course when it is assigned.
          Leave the subject and message blank to skip sending the email.
        </p>
        <div class="msg-field">
          <span class="klabel-medium msg-field-label">Subject</span>
          <input
            type="text"
            class="kinput kbody-medium msg-input-field"
            placeholder="Enter a subject"
            [(ngModel)]="coursePushPackage.messageSubject"
            [ngModelOptions]="{ standalone: true }"
          />
        </div>
        <div class="msg-field">
          <span class="klabel-medium msg-field-label">Message</span>
          <textarea
            [(ngModel)]="coursePushPackage.messageContent"
            [ngModelOptions]="{ standalone: true }"
            rows="6"
            class="kinput msg-textarea"
            placeholder="Start typing something..."
          ></textarea>
        </div>
      </div>
    </ng-container>

    <!-- ── Step: Confirm ──────────────────────────────────────────── -->
    <ng-container *ngIf="currentStep === 'confirm'">
      <div class="section-title">
        <span class="ksubtitle-medium">Review and confirm</span>
        <span class="klabel-medium subtitle">
          We'll create a duplicate of this course for each date selected below.
        </span>
      </div>

      <div class="summary-card">
        <div class="summary-line">
          <span class="klabel-medium label">Source course</span>
          <span class="kbody-medium value">{{ sourceCourseName }}</span>
        </div>
        <div class="summary-line">
          <span class="klabel-medium label">Additional option</span>
          <span class="kbody-medium value">
            {{ pushOption === 'copy-existing' ? 'Copy Existing Registrations' : 'Select New Push Information' }}
          </span>
        </div>

        <!-- Select-new summary fields -->
        <ng-container *ngIf="pushOption === 'select-new'">
          <div class="summary-line" *ngIf="selectedPushGroup">
            <span class="klabel-medium label">Push type</span>
            <span class="kbody-medium value">{{ getPushGroupName(selectedPushGroup) }}</span>
          </div>
          <div class="summary-line" *ngIf="coursePushPackage?.requirement?.requirement">
            <span class="klabel-medium label">Requirement</span>
            <span class="kbody-medium value">{{ coursePushPackage.requirement.requirement }}</span>
          </div>
          <div class="summary-line" *ngIf="coursePushPackage?.buildings?.length > 0">
            <span class="klabel-medium label">Buildings ({{ coursePushPackage.buildings.length }})</span>
            <div class="groups-value">
              <span class="kbody-medium" *ngFor="let b of coursePushPackage.buildings">{{ b.buildingName }}</span>
            </div>
          </div>
          <div class="summary-line" *ngIf="coursePushPackage?.positions?.length > 0">
            <span class="klabel-medium label">Positions ({{ coursePushPackage.positions.length }})</span>
            <div class="groups-value">
              <span class="kbody-medium" *ngFor="let p of coursePushPackage.positions">{{ p.positionTitle }}</span>
            </div>
          </div>
          <div class="summary-line" *ngIf="coursePushPackage?.users?.length > 0">
            <span class="klabel-medium label">Users ({{ coursePushPackage.users.length }})</span>
            <div class="groups-value">
              <span class="kbody-medium" *ngFor="let u of coursePushPackage.users">{{ u.fullName }}</span>
            </div>
          </div>
          <div class="summary-line" *ngIf="coursePushPackage?.messageSubject">
            <span class="klabel-medium label">Subject</span>
            <span class="kbody-medium value">{{ coursePushPackage.messageSubject }}</span>
          </div>
          <div class="summary-line" *ngIf="coursePushPackage?.messageContent">
            <span class="klabel-medium label">Message</span>
            <span class="kbody-medium value">{{ coursePushPackage.messageContent }}</span>
          </div>
        </ng-container>

        <div class="summary-line">
          <span class="klabel-medium label">Number of duplicates</span>
          <span class="kbody-medium value">{{ dateEntries.length }}</span>
        </div>
        <div class="summary-line stacked">
          <span class="klabel-medium label">Dates &amp; times</span>
          <div class="confirm-date-table">
            <div
              class="confirm-date-row"
              *ngFor="let entry of sortedDateEntries"
            >
              <mat-icon
                fontSet="material-icons-round"
                class="date-icon"
              >event</mat-icon>
              <span class="kbody-medium date-label">{{ entry.date | date: 'EEE, MMM d, y' }}</span>
              <span class="klabel-medium time-label">
                {{ formatEntryTime(entry.startTime) }}
                &ndash;
                {{ formatEntryTime(entry.endTime) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Capacity warning banner -->
      <div class="capacity-warning-banner" *ngIf="hasCapacityWarning">
        <div class="capacity-warning-header">
          <mat-icon fontSet="material-icons-round" class="capacity-warn-icon">warning</mat-icon>
          <span class="kbody-medium capacity-warn-title">Capacity may be exceeded</span>
        </div>
        <p class="klabel-medium capacity-warn-body">
          This course has a capacity of <strong>{{ courseCapacity }}</strong>, but this push will target
          approximately <strong>{{ estimatedPushUserCount }}</strong> user{{ estimatedPushUserCount === 1 ? '' : 's' }}.
          Only the first {{ courseCapacity }} user{{ courseCapacity === 1 ? '' : 's' }} to enroll will fill the available slots.
        </p>
        <label class="capacity-warn-ack klabel-medium">
          <input
            type="checkbox"
            [(ngModel)]="capacityWarningAcknowledged"
            [ngModelOptions]="{ standalone: true }"
          />
          I understand and want to proceed anyway
        </label>
      </div>
    </ng-container>

    <!-- ── Submitting ─────────────────────────────────────────────── -->
    <ng-container *ngIf="currentStep === 'submitting'">
      <div class="terminal-state">
        <app-loading-spinner></app-loading-spinner>
        <span class="ksubtitle-medium message">Creating duplicates and pushing...</span>
        <span class="klabel-medium subtitle">This may take a moment.</span>
      </div>
    </ng-container>

    <!-- ── Success ────────────────────────────────────────────────── -->
    <ng-container *ngIf="currentStep === 'success'">
      <div class="terminal-state success">
        <mat-icon fontSet="material-icons-round" class="state-icon success">check_circle</mat-icon>
        <span class="kbody-huge message">Success</span>
        <span class="ksubtitle-medium">
          {{ duplicatedCourses.length }} course{{ duplicatedCourses.length === 1 ? '' : 's' }} created
          <span *ngIf="pushOption === 'select-new' && selectedPushGroup">
            and pushed via <strong>{{ getPushGroupName(selectedPushGroup) }}</strong>
          </span>.
        </span>

        <div class="created-list" *ngIf="duplicatedCourses.length > 0">
          <div class="created-row" *ngFor="let row of duplicatedCourses">
            <mat-icon fontSet="material-icons-round" class="row-icon ok">check</mat-icon>
            <span class="kbody-medium">{{ row.date | date: 'EEE, MMM d, y' }}</span>
            <span class="klabel-medium identifier">Course ID #{{ row.courseItemId }}</span>
            <span class="klabel-medium status" *ngIf="row.groupAssigned">Pushed</span>
          </div>
        </div>

        <div class="inactive-skipped-notice" *ngIf="inactiveUsersSkipped > 0">
          <mat-icon fontSet="material-icons-round" class="notice-icon">info</mat-icon>
          <span class="klabel-medium">
            {{ inactiveUsersSkipped }} inactive user{{ inactiveUsersSkipped === 1 ? ' was' : 's were' }} skipped and did not receive a registration.
          </span>
        </div>
      </div>
    </ng-container>

    <!-- ── Error / partial failure ───────────────────────────────── -->
    <ng-container *ngIf="currentStep === 'error'">
      <div class="terminal-state error">
        <mat-icon fontSet="material-icons-round" class="state-icon error">error</mat-icon>
        <span class="kbody-huge message">We couldn't complete the request</span>
        <span class="ksubtitle-medium error-summary">{{ submitErrorMessage }}</span>

        <div class="failed-list" *ngIf="submitErrorDetails.length > 0">
          <span class="klabel-medium subtitle">Per-date issues</span>
          <div class="failed-row" *ngFor="let detail of submitErrorDetails">
            <mat-icon fontSet="material-icons-round" class="row-icon error">error_outline</mat-icon>
            <div class="failed-row-text">
              <span class="kbody-medium">{{ detail.date | date: 'EEE, MMM d, y' }}</span>
              <span class="klabel-medium reason">{{ detail.reason }}</span>
            </div>
          </div>
        </div>
      </div>
    </ng-container>

  </div>

  <!-- ── Footer navigation ──────────────────────────────────────── -->
  <div class="footer">
    <div class="footer-left">
      <app-button
        *ngIf="currentStep !== 'submitting' && currentStep !== 'success'"
        text="Back"
        [class]="'white'"
        [icon]="{ materialIcon: 'arrow_back' }"
        (click)="goBack()"
      ></app-button>
    </div>

    <div class="footer-right">
      <app-button
        *ngIf="!isOnTerminalStep"
        text="Cancel"
        [class]="'white'"
        (click)="closeModal()"
      ></app-button>

      <!-- Next Step for all workflow screens except confirm -->
      <app-button
        *ngIf="!isOnTerminalStep && currentStep !== 'confirm'"
        text="Next Step"
        [class]="'blue'"
        [disabled]="checkDisabled()"
        [icon]="{ materialIcon: 'arrow_forward' }"
        [reverse]="true"
        (click)="goNext()"
      ></app-button>

      <!-- Confirm & Submit -->
      <app-button
        *ngIf="currentStep === 'confirm'"
        [text]="isSubmitting ? 'Submitting...' : 'Confirm &amp; Submit'"
        [class]="'blue'"
        [disabled]="isSubmitting || dateEntries.length === 0 || !areAllDateTimesComplete || (hasCapacityWarning && !capacityWarningAcknowledged)"
        [icon]="{ materialIcon: 'send' }"
        (click)="submitBatch()"
      ></app-button>

      <app-button
        *ngIf="currentStep === 'success'"
        text="Done"
        [class]="'blue'"
        [icon]="{ materialIcon: 'check' }"
        (click)="finishAndClose()"
      ></app-button>

      <app-button
        *ngIf="currentStep === 'error' && submitErrorDetails.length === 0"
        text="Retry"
        [class]="'blue'"
        [icon]="{ materialIcon: 'refresh' }"
        (click)="retrySubmit()"
      ></app-button>

      <app-button
        *ngIf="currentStep === 'error' && submitErrorDetails.length > 0"
        text="Close &amp; fix manually"
        [class]="'white'"
        (click)="closeModal()"
      ></app-button>
    </div>
  </div>

</div>
